import { createContext, useContext, type MutableRefObject } from 'react';
import type { FlatConfig, SystemConfiguration, Pricing, QuoteMetadata, ViewState } from 'domain/index';
import type { BomRow, CustomPrices, ExecMetricsApi, ExecPricingApi, Metrics, PriceEditionsApi, PricingData, VendorApi } from './types';

function make<T>(name: string) {
  const Ctx = createContext<T | null>(null);
  // Named `useCtx`, not the bare `use`, so eslint-plugin-react-hooks
  // recognises it as a hook (its rule matches `/^use[A-Z0-9]/`) — the
  // exported alias below is what every consumer actually sees.
  const useCtx = (): T => {
    const v = useContext(Ctx);
    if (v === null) throw new Error(`${name} must be used inside <ConfigProvider>.`);
    return v;
  };
  return [Ctx, useCtx] as const;
}

export type VendorCtxValue = VendorApi & { changeVendor: (id: string) => void };
export type ConfigInputs = { config: FlatConfig; system: SystemConfiguration; pricing: Pricing; viewState: ViewState; metadata: QuoteMetadata };
export type ConfigActions = {
  updateConfig: (p: Partial<FlatConfig>) => void;
  batchUpdateConfig: (p: Partial<FlatConfig>) => void;
  loadConfig: (c: Partial<FlatConfig>) => void;
  updateConfigRaw: (p: Partial<FlatConfig>) => void;
  batchUpdateConfigRaw: (p: Partial<FlatConfig>) => void;
};
export type PricingCtxValue = { pricingData: PricingData | null; baselinePricing: unknown; priceBook: unknown; prices: unknown; priceEditions: PriceEditionsApi };
export type ExecCtxValue = {
  execPricing: ExecPricingApi; customPrices: CustomPrices; setCustomPrices: (p: CustomPrices) => void;
  pinSavedPrices: (p: CustomPrices) => void; releasePin: () => void;
  loadedQuoteIdRef: MutableRefObject<string | null>; execPricingTouched: MutableRefObject<boolean>;
  execPanelOpen: boolean; setExecPanelOpen: (open: boolean) => void;
  execMetrics: ExecMetricsApi;
};
/** CONF-212. `metrics` is the DISPLAY figure (exec metric overrides applied) —
 *  what every readout and customer artifact prints. `baselineMetrics` is the
 *  real computed figure; pricing and BOM consumers must read that one.
 *  `affectedKeys` are the keys whose displayed value differs from the real one
 *  (edited keys plus the totals carrying their delta). */
export type MetricsCtxValue = {
  metrics: Metrics | null; baselineMetrics: Metrics | null;
  affectedKeys: ReadonlySet<string>; staleKeys: ReadonlySet<string>;
};
export type PriceEditionState = { promptNewEdition: boolean; staleWarning: boolean; status: string; refresh: () => void; editionNumber: number | null };
export type Toast = { id: string; badge?: string; title: string; message: string };
export type ToastApi = { toasts: Toast[]; showToast: (t: { badge?: string; title: string; message: string; duration?: number }) => void; dismissToast: (id: string) => void };

export const [VendorCtx, useVendorCtx] = make<VendorCtxValue>('useVendorCtx');
export const [ConfigInputsCtx, useConfigInputs] = make<ConfigInputs>('useConfigInputs');
export const [ConfigActionsCtx, useConfigActions] = make<ConfigActions>('useConfigActions');
export const [MetricsCtx, useMetricsCtx] = make<MetricsCtxValue>('useMetrics');
/** Display metrics — overrides applied. For readouts and customer artifacts. */
export const useMetrics = () => useMetricsCtx().metrics;
/** Real computed metrics — for pricing, BOM and part-number consumers (CONF-212 spec §3). */
export const useBaselineMetrics = () => useMetricsCtx().baselineMetrics;
/** Which displayed figures an exec override moved, and which overrides are stale. */
export const useMetricsOverlay = () => {
  const { baselineMetrics, affectedKeys, staleKeys } = useMetricsCtx();
  return { baselineMetrics, affectedKeys, staleKeys };
};
export const [PricingCtx, usePricing] = make<PricingCtxValue>('usePricing');
export const [BomCtx, useBomCtx] = make<{ bom: BomRow[] | null }>('useBom');
export const useBom = () => useBomCtx().bom;
export const [CalcStatusCtx, useCalcStatus] = make<{ isCalculating: boolean }>('useCalcStatus');
export const [ExecCtx, useExecCtx] = make<ExecCtxValue>('useExecCtx');
export const [PriceEditionCtx, usePriceEditionState] = make<PriceEditionState>('usePriceEditionState');
export const [ToastCtx, useToastCtx] = make<ToastApi>('useToastCtx');
